import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  // Optional label used in the console log so it's easy to tell
  // which surface threw (e.g. "loan-request-detail").
  surface?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time exceptions anywhere below it and shows a visible,
 * actionable error UI instead of letting React unmount the tree (which
 * is what causes the "blank page" reports — without this, a thrown
 * error inside a route renders as a white screen).
 *
 * Also logs the error to the console with a `surface` tag so the
 * cause can be pulled from browser logs even after the user has
 * dismissed the screen.
 *
 * Reset on prop change isn't built in — the user clicks Reload to
 * force a fresh route mount. Good enough for production; we can move
 * to react-error-boundary later if we need per-route reset semantics.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary${this.props.surface ? `:${this.props.surface}` : ''}]`,
      error,
      info,
    );
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="flex min-h-[60vh] items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
          <h2 className="mt-3 text-base font-semibold text-amber-900">
            This page hit an unexpected error
          </h2>
          <p className="mt-1.5 text-sm text-amber-800">
            We've logged the details. Reload to try again — if it keeps happening,
            send a screenshot to your HRMS admin with the error message below.
          </p>
          <pre className="mt-4 max-h-32 overflow-auto rounded-md bg-amber-100/60 p-2 text-left text-[11px] text-amber-900">
            {error.message || String(error)}
          </pre>
          <button
            type="button"
            onClick={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
            className="mt-4 inline-flex items-center gap-2 rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
